import React from "react";
import type { ThinkingMode } from "@ms/shared-components/comps/CompsAi";
import type { DataSource } from "../types/aiAssistantTypes";

/**
 * 树节点类型
 * 注意：此接口应该与 @ms/shared-components/comps/CompsAi 中的 TreeNode 接口保持一致
 */
export interface TreeNode {
  id: string;
  label: string;
  type: 'data' | 'thinking';
  description?: string;
  icon?: React.ReactNode;
  data?: any; // 节点关联的数据（门店数据、数据源数据等）
  children?: TreeNode[];
  // 可选：保留原始数据引用
  dataSource?: DataSource;
  thinkingMode?: ThinkingMode;
  [key: string]: any; // 允许扩展属性
}

/**
 * 将数据源转换为树节点
 */
export const dataSourceToTreeNode = (dataSource: DataSource, index: number): TreeNode => {
  // 防御性检查
  if (!dataSource) {
    console.warn(`⚠️ [dataSourceToTreeNode] dataSource 为空，索引: ${index}`);
    return {
      id: `data-unknown-${index}`,
      label: '未知数据源',
      type: 'data',
      children: [], // 确保 children 始终是数组
    };
  }

  return {
    id: `data-${dataSource.id || 'unknown'}-${index}`,
    label: dataSource.metadata?.label || dataSource.id || '未知数据源',
    type: 'data',
    description: dataSource.metadata?.description,
    data: dataSource.data, // 保存数据源的数据
    dataSource,
    // 从 metadata 中提取 componentId，供 CompsAi 从数据库获取思考模式
    componentId: dataSource.metadata?.componentId,
    children: [], // 确保 children 始终是数组
  };
};

/**
 * 将思考方式转换为树节点
 * @param thinkingMode - 思考方式
 * @param index - 索引（用于生成唯一 key）
 * @param parentId - 父节点 ID（可选，用于生成全局唯一的 key）
 */
export const thinkingModeToTreeNode = (
  thinkingMode: ThinkingMode, 
  index: number,
  parentId?: string
): TreeNode => {
  // 防御性检查
  if (!thinkingMode) {
    console.warn(`⚠️ [thinkingModeToTreeNode] thinkingMode 为空，索引: ${index}`);
    const uniqueId = parentId 
      ? `thinking-unknown-${parentId}-${index}`
      : `thinking-unknown-${index}`;
    return {
      id: uniqueId,
      label: '未知思考方式',
      type: 'thinking',
      children: [], // 确保 children 始终是数组
    };
  }

  // 生成全局唯一的 ID：如果提供了 parentId，则包含它以确保唯一性
  const uniqueId = parentId
    ? `thinking-${thinkingMode.id || 'unknown'}-${parentId}-${index}`
    : `thinking-${thinkingMode.id || 'unknown'}-${index}`;

  return {
    id: uniqueId,
    label: thinkingMode.label || '未知思考方式',
    type: 'thinking',
    description: thinkingMode.description,
    thinkingMode,
    children: [], // 确保 children 始终是数组
  };
};

/**
 * 将门店数据转换为树节点（第二层：每家门店）
 */
export const storeToTreeNode = (store: any, index: number): TreeNode => {
  // 防御性检查
  if (!store) {
    console.warn(`⚠️ [storeToTreeNode] store 为空，索引: ${index}`);
    return {
      id: `store-unknown-${index}`,
      label: '未知门店',
      type: 'data',
      children: [], // 确保 children 始终是数组
    };
  }

  return {
    id: `store-${store.id || store.name || 'unknown'}-${index}`,
    label: store.name || store.id || '未知门店',
    type: 'data',
    data: store, // 保存门店的完整数据
    children: [], // 确保 children 始终是数组
  };
};

/**
 * 将数据源列表和思考方式转换为树形结构
 * 支持多层嵌套：
 * - 第一层：数据源（如"客源异常"、"待调价门店"等）
 * - 第二层：门店节点（作为数据源节点的子节点）
 * - 第三层：思考方式节点（作为门店节点的子节点）
 * 
 * @param dataSources - 数据源列表
 * @param thinkingMode - 思考方式（可选）
 * @param clearPrevious - 是否清空之前的内容
 * @param thinkingModes - 思考方式列表（可选，用于为每个门店添加思考方式）
 * 
 * @returns TreeNode[] 树形结构数据
 * 
 * @example
 * ```ts
 * const treeData = buildTreeData(
 *   [dataSource1], // dataSource1.data 包含门店列表
 *   null,
 *   true,
 *   thinkingModes // 思考方式列表
 * );
 * // 结果：
 * // [
 * //   {
 * //     id: 'data-1',
 * //     label: '客源异常',
 * //     type: 'data',
 * //     children: [
 * //       {
 * //         id: 'store-1',
 * //         label: '门店1',
 * //         type: 'data',
 * //         children: [
 * //           { id: 'thinking-analyze', label: '数据分析', type: 'thinking' },
 * //           { id: 'thinking-predict', label: '预测分析', type: 'thinking' },
 * //           { id: 'thinking-optimize', label: '优化建议', type: 'thinking' }
 * //         ]
 * //       },
 * //       {
 * //         id: 'store-2',
 * //         label: '门店2',
 * //         type: 'data',
 * //         children: [...]
 * //       },
 * //       {
 * //         id: 'store-3',
 * //         label: '门店3',
 * //         type: 'data',
 * //         children: [...]
 * //       }
 * //     ]
 * //   }
 * // ]
 * ```
 */
export const buildTreeData = (
  dataSources: DataSource[] | undefined | null,
  thinkingMode?: ThinkingMode | null,
  clearPrevious: boolean = true,
  thinkingModes?: ThinkingMode[] | null
): TreeNode[] => {
  const treeNodes: TreeNode[] = [];

  // 防御性检查：如果 dataSources 为空或未定义，返回空数组
  if (!dataSources || !Array.isArray(dataSources) || dataSources.length === 0) {
    console.warn("⚠️ [buildTreeData] dataSources 为空或未定义，返回空数组");
    return treeNodes;
  }

  // 将每个数据源转换为树节点
  dataSources.forEach((dataSource, index) => {
    // 防御性检查：确保 dataSource 存在
    if (!dataSource) {
      console.warn(`⚠️ [buildTreeData] 索引 ${index} 的数据源为空，跳过`);
      return;
    }

    // 检查数据源的数据是否是数组（如门店列表）
    const sourceData = dataSource.data;
    if (Array.isArray(sourceData) && sourceData.length > 0) {
      // 情况1：数据源包含门店列表
      // 第一层：添加数据源本身（如"客源异常"、"待调价门店"等）
      const dataNode = dataSourceToTreeNode(dataSource, index);
      
      // 第二层：将所有门店作为数据源节点的子节点
      const storeNodes: TreeNode[] = [];
      sourceData.forEach((store, storeIndex) => {
        const storeNode = storeToTreeNode(store, storeIndex);

        // 第三层：如果提供了思考方式列表，为每个门店添加思考方式子节点
        // 使用 storeNode.id 作为 parentId 确保每个门店的思考方式节点有唯一的 key
        if (thinkingModes && Array.isArray(thinkingModes) && thinkingModes.length > 0) {
          storeNode.children = thinkingModes.map((mode, modeIndex) =>
            thinkingModeToTreeNode(mode, modeIndex, storeNode.id)
          );
        } else if (thinkingMode) {
          // 如果只提供了单个思考方式，也添加到每个门店下
          storeNode.children = [
            thinkingModeToTreeNode(thinkingMode, 0, storeNode.id)
          ];
        } else {
          // 确保 children 始终是数组，即使为空
          storeNode.children = storeNode.children || [];
        }

        storeNodes.push(storeNode);
      });
      
      // 将所有门店节点作为数据源节点的子节点
      dataNode.children = storeNodes.length > 0 ? storeNodes : [];
      treeNodes.push(dataNode);
    } else {
      // 情况2：数据源没有子数据（普通数据源）
      const dataNode = dataSourceToTreeNode(dataSource, index);
      
      if (thinkingMode) {
        // 如果没有子数据，直接将思考方式作为数据源的子节点
        // 使用 dataNode.id 作为 parentId 确保唯一性
        dataNode.children = [
          thinkingModeToTreeNode(thinkingMode, 0, dataNode.id)
        ];
      } else if (thinkingModes && Array.isArray(thinkingModes) && thinkingModes.length > 0) {
        // 如果没有子数据但提供了思考方式列表，添加所有思考方式
        // 使用 dataNode.id 作为 parentId 确保唯一性
        dataNode.children = thinkingModes.map((mode, modeIndex) =>
          thinkingModeToTreeNode(mode, modeIndex, dataNode.id)
        );
      } else {
        // 确保 children 始终是数组，即使为空
        dataNode.children = dataNode.children || [];
      }

      treeNodes.push(dataNode);
    }
  });

  return treeNodes;
};

/**
 * 将单个数据源和思考方式转换为树形结构（简化版本）
 * 用于单个数据源的情况
 */
export const buildSingleTreeData = (
  dataSource: DataSource,
  thinkingMode?: ThinkingMode | null
): TreeNode[] => {
  return buildTreeData([dataSource], thinkingMode, true);
};
